import type { MarketParams } from '@morpho-org/blue-sdk';
import type { ActionRequirement, VaultV2BlueReallocation } from '@morpho-org/morpho-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { useQuery } from '@tanstack/react-query';
import type { TransactionBuilder } from '@/contexts/Transaction/TransactionV2.context';
import { describeRequirement } from '@/contexts/Transaction/describeRequirement';
import { useWeb3Context } from '@/contexts/Web3Context';
import { safeSentryBreadcrumb } from '@/helpers/sentry';
import { assertBlueBundlesV1Deployed, assertBuilderIsSigner, buildMarketActionSimulationKey, getMarketActionDeadline, MARKET_ACTION_REBUILD_INTERVAL_MS, type MarketActionKind, type MarketActionMode, MarketOneSidedPositionError, resolveMarketActionKind, resolveRepayAmount } from './marketAction.helpers';
import { useMarketBlue } from './useMarketBlue';
let marketActionBuildSequence = 0;
export interface UseMarketActionBuilderParams {
  /** Target market params (from `apiState.tryGetMarket(marketId)?.params`). */
  marketParams: MarketParams | undefined;
  /** `supplyBorrow` (supply collateral / borrow), `repayWithdraw` (repay / withdraw collateral), `lend` (supply loan assets) or `lendWithdraw` (withdraw supplied loan assets). */
  mode: MarketActionMode;
  /** Total collateral to supply (supplyBorrow) or to withdraw (repayWithdraw), incl. the native part; always 0 in the lend modes. */
  collateralAmount: bigint;
  /** Loan to borrow (supplyBorrow), to repay (repayWithdraw), to lend (lend) or to withdraw (lendWithdraw), incl. the native part. */
  loanAmount: bigint;
  /** Native amount wrapped into the wNative collateral (supply) or loan token (repay / lend); 0 otherwise. */
  nativeAmount?: bigint;
  /** Repay "max"/close → burn exact shares (accrual-immune, S6). */
  isMaxRepay?: boolean;
  /** Lend-withdraw should burn exact supply shares (accrual-immune, S6). */
  shouldUseSupplyShares?: boolean;
  /** Public-allocator reallocations to prepend (WS6); omitted ⇒ none. */
  reallocations?: readonly VaultV2BlueReallocation[];
  /** Fetch the on-chain requirements (permit2 nonce + `getRequirements()` allowance/authorization reads). Panels pass `false` to get the sync SDK build/validation without RPC; review dialogs keep `true`. */
  withRequirements?: boolean;
}

/**
 * Builds one of the morpho-sdk market actions from a panel input pair (WS2),
 * the market-actions-v2 sister of `useVaultV2DepositTransaction`.
 *
 * Routes the inputs to the atomic `client.morpho.blue(...)` method, enforces
 * builder == signer + correct chain before building (S1), and refreshes the
 * position via `useMarketBlue` so the SDK's health validation runs against fresh
 * data. The build runs inside a query so a synchronous SDK guard (e.g.
 * `BorrowExceedsSafeLtvError`) surfaces as a query error rather than crashing
 * render. Returns the unsigned `transaction` builder + its `requirements`
 * (approval / setAuthorization / signature) for `submitTransactions`.
 */
export function useMarketActionBuilder({
  marketParams,
  mode,
  collateralAmount,
  loanAmount,
  nativeAmount = ZERO,
  isMaxRepay = false,
  shouldUseSupplyShares = false,
  reallocations,
  withRequirements = true
}: UseMarketActionBuilderParams) {
  const {
    account,
    viemClient,
    targetChainId
  } = useWeb3Context();
  const {
    market,
    positionData,
    isLoading: isPositionLoading,
    error: marketError
  } = useMarketBlue(marketParams);
  const kind: MarketActionKind = resolveMarketActionKind({
    collateralAmount,
    loanAmount,
    mode
  });

  // Health-sensitive actions need a fresh position; supplyCollateral does not.
  const needsPosition = kind !== 'supplyCollateral' && kind !== 'none';
  const isPreparedEnabled = kind !== 'none' && !!market && !!account.address && (!needsPosition || !!positionData);

  // Build the action AND its on-chain requirements in ONE query so they are always a consistent
  // pair. When they lived in two chained queries (each with `keepPreviousData`), the built
  // `transaction` and its `requirements` — the latter being the source of the simulation's approval
  // / `setAuthorization` prefix — could settle to DIFFERENT generations during a refetch: a new
  // action that needs authorization could pair with the previous, empty `requirements`, so the
  // review simulation ran the borrow/withdraw with a missing prefix → `unauthorized` revert (and
  // only succeeded on the re-run once requirements caught up). Building both together makes that
  // desync structurally impossible: `requirements` is always `getRequirements()` of the exact
  // `action` returned alongside it.
  const {
    data: prepared,
    isLoading: isActionLoading,
    error: buildError,
    isPlaceholderData,
    isFetching,
    refetch
  } = useQuery({
    // Rebuild on any input/position/client change; positionData re-keys it each poll.
    queryKey: ['market-action-prepared', kind, marketParams?.id, account.address, collateralAmount, loanAmount, nativeAmount, isMaxRepay, reallocations, positionData, targetChainId, viemClient,
    // Appended (not inserted) so the placeholderData index map below stays valid.
    shouldUseSupplyShares, withRequirements],
    queryFn: async () => {
      // S1 tripwire: the builder always uses the connected `account.address` as the SDK
      // `userAddress` (below) and `viemClient` is bound to `targetChainId`, so this cannot
      // fail as-wired — it documents the invariant and would only fire if a future caller
      // built for a non-connected address. The authoritative builder==signer + chain check
      // runs at submit time against the real wallet client (`validateClientParams` in
      // TransactionV2.context).
      assertBuilderIsSigner({
        userAddress: account.address,
        accountAddress: account.address,
        clientChainId: viemClient.chain?.id,
        expectedChainId: targetChainId
      });
      await assertBlueBundlesV1Deployed(viemClient, targetChainId);
      const userAddress = account.address!;

      // v6 BlueBundlesV1 writes each require an explicit `deadline` (BlueBundlesV1 reverts past it,
      // and it is baked into the Permit2 / `setAuthorization` signatures). Stamp it once so the
      // review sim and submit share one wall-clock value across the placeholder window.
      const deadline = getMarketActionDeadline();

      // v6 native max-close corner: when the exact-shares close is funded with native ETH,
      // `nativeAmount` must equal the SDK-derived `maxRepayAssets` (debt forward-accrued to
      // `deadline` + referral fee), NOT the entered amount, or
      // `validateBlueBundlesV1NativeFunding` throws `NativeFundingAmountMismatchError`.
      // Derive it from a dry ERC-20 build for the exact-shares close.
      const deriveMaxRepayAssets = () => market!.repay({
        userAddress,
        positionData: positionData!,
        repayShares: positionData!.borrowShares,
        deadline
      }).buildTx().action.args.maxRepayAssets;
      const buildAction = async () => {
        // v6 native funding is EXCLUSIVE (all-or-nothing): `collateralAssets`/`assets`/repay legs are
        // GROSS and `nativeAmount` is either 0 or the full funded leg (already resolved upstream by
        // `computeNativeWrapSplit`).
        switch (kind) {
          case 'supplyCollateral':
            return market!.supplyCollateral({
              userAddress,
              collateralAssets: collateralAmount,
              nativeAmount,
              deadline
            });
          case 'supply':
            // The one-sided rule's last line: every lend passes through here with the
            // freshest position, however its entry point was gated.
            if (positionData!.borrowShares > ZERO || positionData!.collateral > ZERO) {
              throw new MarketOneSidedPositionError();
            }
            return market!.supply({
              userAddress,
              assets: loanAmount,
              nativeAmount,
              deadline
            });
          case 'withdraw':
            // Exact-shares on MAX when local liquidity covers the burn (S6 accrual immunity);
            // otherwise assets. The plan rides in both modes
            // (see shouldUseExactSharesForMaxLendWithdraw).
            return market!.withdraw({
              userAddress,
              positionData: positionData!,
              ...(shouldUseSupplyShares ? {
                shares: positionData!.supplyShares
              } : {
                assets: loanAmount
              }),
              reallocations,
              deadline
            });
          case 'borrow':
            return market!.borrow({
              userAddress,
              borrowAssets: loanAmount,
              positionData: positionData!,
              reallocations,
              deadline
            });
          case 'supplyCollateralBorrow':
            return market!.supplyCollateralBorrow({
              userAddress,
              collateralAssets: collateralAmount,
              borrowAssets: loanAmount,
              positionData: positionData!,
              nativeAmount,
              reallocations,
              deadline
            });
          case 'repay':
            return market!.repay({
              userAddress,
              positionData: positionData!,
              ...resolveRepayAmount({
                isMaxRepay,
                repayAssets: loanAmount,
                borrowShares: positionData!.borrowShares,
                nativeAmount: isMaxRepay && nativeAmount > ZERO ? deriveMaxRepayAssets() : nativeAmount
              }),
              deadline
            });
          case 'withdrawCollateral':
            return market!.withdrawCollateral({
              userAddress,
              positionData: positionData!,
              collateralAssets: collateralAmount,
              deadline
            });
          case 'repayWithdrawCollateral':
            return market!.repayWithdrawCollateral({
              userAddress,
              positionData: positionData!,
              ...resolveRepayAmount({
                isMaxRepay,
                repayAssets: loanAmount,
                borrowShares: positionData!.borrowShares,
                nativeAmount: isMaxRepay && nativeAmount > ZERO ? deriveMaxRepayAssets() : nativeAmount
              }),
              collateralAssets: collateralAmount,
              deadline
            });
          default:
            return null;
        }
      };
      const action = await buildAction();
      let requirements: readonly ActionRequirement[] = [];
      if (withRequirements && action && 'getRequirements' in action) {
        requirements = await action.getRequirements();
      }
      const buildSeq = ++marketActionBuildSequence;
      if (withRequirements) {
        safeSentryBreadcrumb({
          category: 'transaction',
          message: 'Market action requirements built',
          level: 'info',
          data: {
            chainId: targetChainId,
            kind,
            supportSignature: viemClient.morpho?.options.supportSignature,
            requirementCount: requirements.length,
            requirementKinds: requirements.map(describeRequirement).join(','),
            buildSeq
          }
        });
      }
      // Stamp the sim key from the same inputs `action` was built from, atomically with it, so the
      // review sim and the submitted tx never diverge. See `buildMarketActionSimulationKey`.
      const simulationKey = buildMarketActionSimulationKey({
        kind,
        collateralAmount,
        loanAmount,
        nativeAmount,
        isMaxRepay,
        shouldUseSupplyShares,
        positionData,
        reallocations
      });
      return {
        action,
        requirements,
        simulationKey,
        deadline
      };
    },
    enabled: isPreparedEnabled,
    // The stamped `deadline` is baked into the built calldata/signatures; rebuild the prepared
    // bundle 5 min before it expires so a cached action never sits "ready" past expiry.
    refetchInterval: MARKET_ACTION_REBUILD_INTERVAL_MS,
    refetchIntervalInBackground: true,
    // Keep the prepared bundle across SAFE refreshes only — a new snapshot block (positionData
    // re-key) or the same signer editing the inputs — so the review simulation doesn't flash a
    // skeleton. DROP it the instant the signer / chain / client / market changes: plain
    // `keepPreviousData` would keep exposing the PREVIOUS signer's `action` + `requirements` as
    // ready (`areRequirementsReady`), so Confirm could submit a bundle built for the prior signer
    // before the rebuilt queryFn re-runs `assertBuilderIsSigner` — breaking the builder==signer
    // invariant (S1) for a funds-moving flow. Returning `undefined` makes the query report loading
    // so `areRequirementsReady` / `transaction` gate the simulation and submit correctly (mirrors
    // `useMarketBlue`). queryKey layout (keep this index map in sync): [0]=label, [1]=kind,
    // [2]=marketParams.id, [3]=account.address, [4]=collateralAmount, [5]=loanAmount,
    // [6]=nativeAmount, [7]=isMaxRepay, [8]=reallocations, [9]=positionData,
    // [10]=targetChainId, [11]=viemClient, [12]=shouldUseSupplyShares,
    // [13]=withRequirements.
    placeholderData: (previousData, previousQuery) => {
      if (previousData == null || !previousQuery) return undefined;
      const [,, prevMarketId, prevAccount] = previousQuery.queryKey;
      const prevChainId = previousQuery.queryKey[10];
      const prevClient = previousQuery.queryKey[11];
      return prevMarketId === marketParams?.id && prevAccount === account.address && prevChainId === targetChainId && prevClient === viemClient ? previousData : undefined;
    }
  });
  const action_0 = prepared?.action ?? null;

  // The prepared bundle is "ready" once the query has produced a result for the current inputs (or
  // there is nothing to prepare — incomplete inputs / missing market). `keepPreviousData` keeps
  // `prepared` defined across refetches, and because `action` + `requirements` are built together
  // the kept value is always an internally-consistent pair, so the review simulation can safely run
  // against it without the `unauthorized` revert a stale, mismatched prefix used to cause.
  const areRequirementsReady = !isPreparedEnabled || prepared !== undefined;
  return {
    transaction: (action_0 ?? undefined) as TransactionBuilder | undefined,
    requirements: prepared?.requirements,
    /** True once the action + its `getRequirements()` are built — gate the simulation on this. */
    areRequirementsReady: areRequirementsReady && !isPlaceholderData,
    isRequirementsPlaceholderData: isPlaceholderData,
    /** Content signature of the built bundle, stamped atomically with `transaction`; feed to
     * `useMarketSimulatedFinalPosition` as `simulationKey`. */
    simulationKey: prepared?.simulationKey,
    /** Deadline baked into the built calldata/signatures; submit is gated on it. */
    deadline: prepared?.deadline,
    /** Rebuilds the prepared bundle (e.g. when its stamped deadline is too close). */
    rebuild: refetch,
    /**
     * True while the prepared bundle is being (re)built, including the background rebuild every
     * block poll triggers. A refresh signal, not a block: feed it to `useReviewConfirmGate`'s
     * `isRefreshSignal`, never to a disabled condition, or Confirm greys out every poll.
     */
    isRefreshing: isFetching,
    kind,
    positionData,
    /** Latest-block or position-read error; cached position data must not remain actionable. */
    marketError,
    /** Build-time error (e.g. morpho-sdk `BorrowExceedsSafeLtvError`) for the caller to surface. */
    buildError,
    isLoading: isPositionLoading || isActionLoading
  };
}